import {useEffect,useLayoutEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import type {ReactNode} from 'react';
import type {Pane as PaneName} from '../state';

/** Body-owned popover avoids pane clipping and dismisses over opaque transcript iframes. */
export function ToolbarMenu({label,icon,children,className='',closeKey}:{label:string;icon:ReactNode;children:ReactNode;className?:string;closeKey?:unknown}){
  const anchor=useRef<HTMLButtonElement>(null),popup=useRef<HTMLDivElement>(null);
  const [open,setOpen]=useState(false),[position,setPosition]=useState({left:12,top:52,width:300});
  function close(){setOpen(false);anchor.current?.focus();}
  useEffect(()=>setOpen(false),[closeKey]);
  useLayoutEffect(()=>{
    if(!open||!anchor.current)return;
    const rect=anchor.current.getBoundingClientRect(),width=Math.min(320,window.innerWidth-24);
    const height=Math.min(popup.current?.scrollHeight||300,window.innerHeight-24);
    setPosition({left:Math.max(12,Math.min(rect.right-width,window.innerWidth-width-12)),top:rect.bottom+height+8<window.innerHeight?rect.bottom+6:Math.max(12,rect.top-height-6),width});
    popup.current?.querySelector<HTMLElement>('button:not(:disabled),input,select,a')?.focus();
  },[open]);
  useEffect(()=>{if(!open)return;const dismiss=()=>setOpen(false);window.addEventListener('resize',dismiss);return()=>window.removeEventListener('resize',dismiss);},[open]);
  return <div className={`toolbar-menu ${className}`}>
    <button ref={anchor} type="button" aria-label={label} title={label} aria-expanded={open} className="chrome-button" onClick={()=>setOpen(value=>!value)}>{icon}</button>
    {open&&createPortal(<div className="toolbar-menu-layer" onPointerDown={event=>{if(event.target===event.currentTarget)close();}}>
      <div ref={popup} className="popover-menu" role="dialog" aria-modal="true" aria-label={label} style={position}
        onClick={event=>{if((event.target as HTMLElement).closest('[data-menu-action]'))close();}}
        onKeyDown={event=>{
          if(event.key==='Escape'){event.stopPropagation();close();}
          if(event.key==='Tab'){
            const controls=[...(popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input,select,a[href]')||[])];
            const first=controls[0],last=controls.at(-1);
            if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
            else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
          }
        }}>{children}</div>
    </div>,document.body)}
  </div>;
}

export function ResizeHandle({pane,width,onChange,reverse=false}:{pane:PaneName;width:number;onChange:(width:number)=>void;reverse?:boolean}){
  const start=useRef({x:0,width:0});
  function actualWidth(handle:HTMLElement){
    let sibling=reverse?handle.nextElementSibling:handle.previousElementSibling;
    while(sibling&&(!(sibling instanceof HTMLElement)||sibling.hidden||!sibling.classList.contains('workspace-pane')))sibling=reverse?sibling.nextElementSibling:sibling.previousElementSibling;
    return sibling?.getBoundingClientRect().width||width;
  }
  useEffect(()=>()=>document.body.classList.remove('is-resizing'),[]);
  return <div className="resize-handle" role="separator" aria-label={`调整${pane==='library'?'资料库':pane==='reader'?'阅读器':'对话'}宽度`} aria-orientation="vertical" aria-valuemin={260} aria-valuemax={1400} aria-valuenow={Math.round(width)} tabIndex={0}
    onKeyDown={event=>{if(event.key==='ArrowLeft'||event.key==='ArrowRight'){event.preventDefault();onChange(actualWidth(event.currentTarget)+(event.key==='ArrowRight'?24:-24)*(reverse?-1:1));}}}
    onPointerDown={event=>{event.preventDefault();start.current={x:event.clientX,width:actualWidth(event.currentTarget)};event.currentTarget.setPointerCapture(event.pointerId);document.body.classList.add('is-resizing');}}
    onPointerMove={event=>{if(event.currentTarget.hasPointerCapture(event.pointerId))onChange(start.current.width+(event.clientX-start.current.x)*(reverse?-1:1));}}
    onPointerUp={event=>{event.currentTarget.releasePointerCapture(event.pointerId);document.body.classList.remove('is-resizing');}}
    onLostPointerCapture={()=>document.body.classList.remove('is-resizing')}><span/></div>;
}
